import React, { useState, useEffect, useMemo, useCallback, useRef, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Briefcase, User } from 'lucide-react';
import { useData } from '../contexts/DataContext'; // Use context data
import { lockScroll, unlockScroll } from '../services/scrollLock';
import { getProjectPath } from '../utils/projectSlug';

export const CommandPalette: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedIndexRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { projects } = useData();
  const deferredSearch = useDeferredValue(search);
  const updateSelectedIndex = useCallback((value: number | ((prev: number) => number)) => {
    setSelectedIndex((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      selectedIndexRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || e.key === '/') {
        e.preventDefault();
        setIsOpen((open) => !open);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleOpenSearch = () => {
      setIsOpen(true);
    };

    document.addEventListener('keydown', down);
    window.addEventListener('open-command-palette', handleOpenSearch);

    return () => {
      document.removeEventListener('keydown', down);
      window.removeEventListener('open-command-palette', handleOpenSearch);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    lockScroll();
    return () => unlockScroll();
  }, [isOpen]);

  // Hide the floating chat launcher while the palette is open, reusing the
  // ChatWidget's modal-opened/modal-closed mechanism.
  useEffect(() => {
    if (!isOpen) return undefined;
    window.dispatchEvent(new Event('modal-opened'));
    return () => {
      window.dispatchEvent(new Event('modal-closed'));
    };
  }, [isOpen]);

  // Autofocus the search input on open — but only on desktop. On phones, focusing
  // pops the on-screen keyboard before the user asks for it, so we skip it below
  // 768px (matchMedia is read at open time). Tapping the field still focuses normally.
  useEffect(() => {
    if (!isOpen) return;
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const commands = useMemo(() => ([
    {
      category: 'Navigation',
      items: [
        { icon: <Briefcase size={16}/>, label: 'Go to Portfolio', type: 'Page', action: () => navigate('/portfolio') },
        { icon: <User size={16}/>, label: 'Go to About Me', type: 'Page', action: () => navigate('/about') },
      ]
    },
    {
      category: 'Projects',
      items: projects.map(p => ({
        // Logo sits object-contain inside the uniform ringed tile (rendered below),
        // so the left rail stays a clean column regardless of logo aspect ratio.
        icon: <img src={p.logoUrl} className="h-[18px] w-[18px] object-contain" alt="" />,
        label: p.title || p.name || 'Untitled',
        type: 'Project',
        action: () => navigate(getProjectPath(p))
      }))
    }
  ]), [navigate, projects]);

  const filteredCommands = useMemo(() => commands.map(group => ({
    ...group,
    items: group.items.filter(item => item.label.toLowerCase().includes(deferredSearch.toLowerCase()))
  })).filter(group => group.items.length > 0), [commands, deferredSearch]);

  const flatItems = useMemo(() => filteredCommands.flatMap(group => group.items), [filteredCommands]);

  const handleSelect = useCallback((action: () => void) => {
    action();
    setIsOpen(false);
    setSearch('');
  }, []);

  useEffect(() => {
    updateSelectedIndex(0);
  }, [flatItems.length, updateSelectedIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || flatItems.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        updateSelectedIndex(i => (i + 1) % flatItems.length);
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        updateSelectedIndex(i => (i - 1 + flatItems.length) % flatItems.length);
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const currentIndex = selectedIndexRef.current;
        if (flatItems[currentIndex]) {
          handleSelect(flatItems[currentIndex].action);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, flatItems, handleSelect, updateSelectedIndex]);

  // While a query is active we collapse the grouped view into one clean ranked
  // list (no category labels); at rest we show the labelled groups.
  const isSearching = deferredSearch.trim().length > 0;

  // Single row renderer shared by the grouped (at rest) and flat (searching)
  // views. flatIndex is passed in (not derived) so both views stay in sync with
  // keyboard selection.
  const renderRow = (item: (typeof flatItems)[number], flatIndex: number) => {
    const isSelected = flatIndex === selectedIndex;
    return (
      <div
        key={flatIndex}
        onClick={() => handleSelect(item.action)}
        onMouseEnter={() => updateSelectedIndex(flatIndex)}
        className={`relative mx-2 px-3 py-2.5 rounded-xl flex items-center justify-between cursor-pointer transition-colors ${
          isSelected
            ? 'bg-white/[0.06] [.nardogrey_&]:bg-white/[0.09]'
            : 'hover:bg-black/5 dark:hover:bg-white/5 [.nardogrey_&]:hover:bg-white/5'
        }`}
      >
        {/* 2px monochrome left accent bar marks the selected row (absolute → no layout shift) */}
        {isSelected && (
          <span aria-hidden className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-white" />
        )}
        <div className="flex items-center gap-3 min-w-0">
          {/* Uniform ringed tile — mirrors About's tiles, smaller; keeps the glyph column even */}
          <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-white/[0.05] ring-1 ring-white/10 text-zinc-600 dark:text-zinc-300 [.nardogrey_&]:text-white/80">
            {item.icon}
          </span>
          <span className="truncate text-sm font-medium text-zinc-700 dark:text-zinc-200 [.nardogrey_&]:text-white/90">
            {item.label}
          </span>
        </div>
        <div className="flex items-center gap-3 shrink-0 pl-3">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 [.nardogrey_&]:text-white/40">
            {item.type}
          </span>
          {/* Only the selected row carries a trailing glyph; no per-row arrow otherwise */}
          {isSelected && (
            <span aria-hidden className="text-xs text-zinc-400 [.nardogrey_&]:text-white/50">↵</span>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-3 sm:px-4 animate-fade-in transition-transform duration-150 ease-out">
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-md"
        onClick={() => setIsOpen(false)}
      />
      <div className="relative w-full max-w-2xl sm:max-w-3xl bg-zinc-50/95 dark:bg-zinc-900/95 [.nardogrey_&]:bg-[#686A6C]/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/40 dark:border-white/10 overflow-hidden flex flex-col max-h-[70vh] sm:max-h-[75vh] will-change-transform will-change-opacity">
        <div className="flex items-center gap-3.5 px-5 sm:px-6 py-4 sm:py-5 border-b border-black/5 dark:border-white/5">
          <Search className="text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-white/60 w-5 h-5 sm:w-[22px] sm:h-[22px] shrink-0" />
          <input
            ref={inputRef}
            className="flex-1 bg-transparent outline-none text-zinc-900 dark:text-white placeholder-zinc-500 dark:placeholder-zinc-500 [.nardogrey_&]:placeholder-white/50 text-base sm:text-xl"
            placeholder="Type a command or search..."
            value={search}
            onChange={e => { setSearch(e.target.value); updateSelectedIndex(0); }}
          />
          <div className="flex items-center gap-1">
            <span className="text-[10px] bg-black/5 dark:bg-white/10 [.nardogrey_&]:bg-white/20 px-1.5 py-0.5 rounded text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-white/80">ESC</span>
          </div>
        </div>

        <div data-lenis-prevent className="overflow-y-auto py-3 flex-1 scrollbar-hide">
          {flatItems.length === 0 ? (
             <div className="p-8 text-center text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-white/60 text-sm">No results found.</div>
          ) : isSearching ? (
            // Searching: one clean ranked list, no category labels
            flatItems.map((item, i) => renderRow(item, i))
          ) : (
            // At rest: labelled groups. flatIndex is tracked via a running offset
            // so keyboard selection lines up with the flat item order.
            (() => {
              let offset = 0;
              return filteredCommands.map((group, gIdx) => {
                const start = offset;
                offset += group.items.length;
                return (
                  <div key={gIdx} className="mb-3">
                    <div className="px-5 sm:px-6 pt-1 pb-2 text-[10px] uppercase tracking-wider font-semibold text-zinc-400 [.nardogrey_&]:text-white/40">
                      {group.category}
                    </div>
                    {group.items.map((item, idx) => renderRow(item, start + idx))}
                  </div>
                );
              });
            })()
          )}
        </div>

        <div className="bg-zinc-100/50 dark:bg-black/20 px-5 sm:px-6 py-2.5 border-t border-black/5 dark:border-white/5 flex justify-between items-center text-[10px] text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-white/50">
           <div className="flex items-center gap-4">
             <span><kbd className="font-sans">↑↓</kbd> navigate</span>
             <span><kbd className="font-sans">↵</kbd> select</span>
           </div>
           <span className="uppercase tracking-widest text-zinc-400 dark:text-zinc-500 [.nardogrey_&]:text-white/40">HELENENDORFSKY OS</span>
        </div>
      </div>
    </div>
  );
};
